import {
    Autocomplete,
    Box,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
} from '@mui/material';
import { FunctionComponent } from 'react';
import { ColorInput } from '@/components/Layout/ColorInput';
import { WidgetParameter } from '@/models/widget';
import { FONT_OPTIONS, humanizeLabel } from '@/pages/widgetEditor/utils';

interface WidgetParameterControlProps {
    name: string;
    param: WidgetParameter;
    value: string;
    /** Shown read-only when the instance follows the shared design. */
    disabled?: boolean;
    onChange: (value: string) => void;
}

/** The editor control matching a manifest parameter's declared type. */
export const WidgetParameterControl: FunctionComponent<
    WidgetParameterControlProps
> = ({ name, param, value, disabled = false, onChange }) => {
    const label = humanizeLabel(name);

    if (param.type === 'font') {
        return (
            <Autocomplete
                freeSolo
                fullWidth
                disableClearable
                disabled={disabled}
                options={FONT_OPTIONS}
                // The default filter narrows the list to options matching
                // the current text, so once a font is selected, opening the
                // dropdown shows only that one. This is a fixed suggestion
                // list, not a search box, so it always shows every option.
                filterOptions={(options) => options}
                inputValue={value}
                onInputChange={(_, newValue) => onChange(newValue)}
                renderOption={({ key, ...props }, option) => (
                    <Box
                        component='li'
                        key={key}
                        {...props}
                        sx={{ fontFamily: option }}
                    >
                        {option}
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        helperText={param.description}
                        inputProps={{
                            ...params.inputProps,
                            style: { fontFamily: value },
                        }}
                    />
                )}
            />
        );
    }

    if (param.type === 'color') {
        return (
            <ColorInput
                fullWidth
                disabled={disabled}
                label={label}
                helperText={param.description}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }

    if (param.type === 'boolean') {
        return (
            <FormControlLabel
                control={
                    <Switch
                        disabled={disabled}
                        checked={value === 'true'}
                        onChange={(e) => onChange(String(e.target.checked))}
                    />
                }
                label={label}
            />
        );
    }

    if (param.type === 'enum' || param.options?.length) {
        return (
            <TextField
                select
                fullWidth
                disabled={disabled}
                label={label}
                helperText={param.description}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            >
                {param.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    return (
        <TextField
            fullWidth
            disabled={disabled}
            type={param.type === 'number' ? 'number' : 'text'}
            label={label}
            helperText={param.description}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputProps={param.type === 'number' ? { min: 0 } : undefined}
        />
    );
};
